import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Alert,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Divider,
  IconButton
} from '@mui/material';
import { Save as SaveIcon, ArrowBack as ArrowBackIcon, Refresh as RefreshIcon, Launch as LaunchIcon, LinkedIn as LinkedInIcon, Facebook as FacebookIcon } from '@mui/icons-material';
import { contactService } from '../services/contactService';
import { useCurrentUser } from '../hooks/useCurrentUser';
import UserDisplay from '../components/UserDisplay';
import CompanySelector from '../components/CompanySelector';
import AssociatedOpportunities from '../components/AssociatedOpportunities';
import OpportunityMapSection from '../components/OpportunityMapSection';
import { logError, formatErrorMessage } from '../utils/errorLogger';

const Contact = () => {
  const { contactId } = useParams();
  const navigate = useNavigate();
  const currentUser = useCurrentUser();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [contact, setContact] = useState({
    firstName: '',
    lastName: '',
    title: '',
    companyId: '',
    email: '',
    mainPhone: '',
    secondaryPhone: '',
    fax: '',
    street1: '',
    street2: '',
    city: '',
    province: '',
    country: '',
    postalCode: '',
    notes: '',
    linkedInProfile: '',
    facebookPage: '',
    createdByEmail: '',
    createdDate: new Date().toISOString()
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [validationErrors, setValidationErrors] = useState({});

  const isEditing = contactId !== 'new';

  const loadContact = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      contactService.validateConfiguration();
      const contactData = await contactService.getContact(contactId);
      if (contactData) {
        // Ensure all string fields are initialized as empty strings instead of null
        const sanitizedContact = Object.keys(contactData).reduce((acc, key) => {
          acc[key] = contactData[key] === null ? '' : contactData[key];
          return acc;
        }, {});
        setContact(sanitizedContact);
      } else {
        setError('Contact not found');
      }
    } catch (err) {
      logError('Contact', 'loadContact', err, { contactId });
      setError(formatErrorMessage(err, 'Failed to load contact details'));
    } finally {
      setLoading(false);
    }
  }, [contactId]);

  useEffect(() => {
    if (isEditing) {
      loadContact();
    }
  }, [contactId, isEditing, loadContact]);

  // Debug: Log contact data when it changes
  useEffect(() => {
    if (contact && contact.CreatedBy) {
      console.log('🔍 [Contact] CreatedBy field value:', contact.CreatedBy);
      console.log('🔍 [Contact] CreatedBy field type:', typeof contact.CreatedBy);
    }
  }, [contact]);

  // Note: Auto-refresh removed from individual entity pages to prevent data loss
  // Manual refresh is still available via the refresh button

  const handleInputChange = (field) => (event) => {
    const value = event.target.value ?? ''; // Ensure null values are converted to empty strings
    setContact(prev => ({ ...prev, [field]: value }));
    
    // Clear validation error when user starts typing
    if (validationErrors[field]) {
      setValidationErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!contact.firstName.trim()) {
      errors.firstName = 'First Name is required';
    }
    
    if (!contact.lastName.trim()) {
      errors.lastName = 'Last Name is required';
    }

    if (contact.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contact.email)) {
      errors.email = 'Please enter a valid email address';
    }

    // Validate LinkedIn Profile URL
    if (contact.linkedInProfile && contact.linkedInProfile.trim()) {
      const linkedInPattern = /^https?:\/\/(www\.)?linkedin\.com\/.*$/i;
      if (!linkedInPattern.test(contact.linkedInProfile.trim())) {
        errors.linkedInProfile = 'Please enter a valid LinkedIn URL (e.g., https://linkedin.com/in/username)';
      }
    }

    // Validate Facebook Page URL
    if (contact.facebookPage && contact.facebookPage.trim()) {
      const facebookPattern = /^https?:\/\/(www\.)?facebook\.com\/.*$/i;
      if (!facebookPattern.test(contact.facebookPage.trim())) {
        errors.facebookPage = 'Please enter a valid Facebook URL (e.g., https://facebook.com/username)';
      }
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    setSaving(true);
    setError('');
    setSuccess('');

    try {
      // Validate configuration first
      contactService.validateConfiguration();
      await contactService.saveContact(contact, currentUser.email);
      setSuccess(isEditing ? 'Contact updated successfully' : 'Contact created successfully');
      
      // Navigate back to contacts list after a short delay
      setTimeout(() => {
        navigate('/contacts');
      }, 1500);
    } catch (err) {
      logError('Contact', 'saveContact', err, { contact });
      setError(formatErrorMessage(err, 'Failed to save contact'));
    } finally {
      setSaving(false);
    }
  };

  const handleBack = () => {
    navigate('/contacts');
  };

  const handleGoogleMapsClick = () => {
    const addressParts = [];
    if (contact.street1) addressParts.push(contact.street1);
    if (contact.street2) addressParts.push(contact.street2);
    if (contact.city) addressParts.push(contact.city);
    if (contact.province) addressParts.push(contact.province);
    if (contact.country) addressParts.push(contact.country);
    if (contact.postalCode) addressParts.push(contact.postalCode);
    
    const fullAddress = addressParts.join(', ');
    if (fullAddress) {
      const googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(fullAddress)}`;
      window.open(googleMapsUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const handleSocialMediaClick = (url, platform) => {
    if (url && url.trim()) {
      // Ensure URL has protocol
      let fullUrl = url.trim();
      if (!fullUrl.startsWith('http://') && !fullUrl.startsWith('https://')) {
        fullUrl = `https://${fullUrl}`;
      }
      window.open(fullUrl, '_blank', 'noopener,noreferrer');
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', pr: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
            sx={{ mr: 2 }}
          >
            Back to Contacts
          </Button>
          <Typography variant="h4">
            {isEditing ? 'Edit Contact' : 'Create Contact'}
          </Typography>
        </Box>
        
        {/* Refresh Button - Only show for existing contacts */}
        {isEditing && (
          <IconButton
            onClick={() => loadContact()}
            disabled={loading}
            title="Refresh data"
            sx={{ color: 'primary.main' }}
          >
            <RefreshIcon />
          </IconButton>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <Paper elevation={3} sx={{ p: 3 }}>
        <Grid container spacing={isMobile ? 2 : 3}>
          {/* First Name */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="First Name"
              value={contact.firstName}
              onChange={handleInputChange('firstName')}
              error={!!validationErrors.firstName}
              helperText={validationErrors.firstName}
              required
            />
          </Grid>

          {/* Last Name */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Last Name"
              value={contact.lastName}
              onChange={handleInputChange('lastName')}
              error={!!validationErrors.lastName}
              helperText={validationErrors.lastName}
              required
            />
          </Grid>

          {/* Title */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Title"
              value={contact.title}
              onChange={handleInputChange('title')}
            />
          </Grid>

          {/* Company */}
          <Grid item xs={12} md={6}>
            <CompanySelector
              value={contact.companyId}
              onChange={(companyId) => setContact(prev => ({ ...prev, companyId: companyId }))}
              error={!!validationErrors.companyId}
              helperText={validationErrors.companyId}
              getOptionLabel={(option) => option.companyName || ''}
            />
          </Grid>

          {/* Email */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={contact.email}
              onChange={handleInputChange('email')}
              error={!!validationErrors.email}
              helperText={validationErrors.email}
            />
          </Grid>

          {/* Main Phone */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Main Phone"
              value={contact.mainPhone}
              onChange={handleInputChange('mainPhone')}
            />
          </Grid>

          {/* Secondary Phone */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Secondary Phone"
              value={contact.secondaryPhone}
              onChange={handleInputChange('secondaryPhone')}
            />
          </Grid>

          {/* Fax */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Fax"
              value={contact.fax}
              onChange={handleInputChange('fax')}
            />
          </Grid>

          {/* Address Section */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', my: 2 }}>
              <Divider sx={{ flexGrow: 1 }}>
                <Typography variant="h6" color="text.secondary">
                  Address Information
                </Typography>
              </Divider>
            </Box>
          </Grid>

          {/* Street 1 */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Street 1"
              value={contact.street1}
              onChange={handleInputChange('street1')}
            />
          </Grid>

          {/* Street 2 */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Street 2"
              value={contact.street2}
              onChange={handleInputChange('street2')}
            />
          </Grid>

          {/* City */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="City"
              value={contact.city}
              onChange={handleInputChange('city')}
            />
          </Grid>

          {/* Province */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Province/State"
              value={contact.province}
              onChange={handleInputChange('province')}
            />
          </Grid>

          {/* Country */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Country"
              value={contact.country}
              onChange={handleInputChange('country')}
            />
          </Grid>

          {/* Postal Code */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Postal Code/Zip Code"
              value={contact.postalCode}
              onChange={handleInputChange('postalCode')}
            />
          </Grid>

          {/* Map Section */}
          <Grid item xs={12}>
            <Divider sx={{ my: 2 }}>
              <Typography variant="h6" color="text.secondary">
                Map
              </Typography>
            </Divider>

            <OpportunityMapSection company={contact} contacts={[]} />
          </Grid>

          {/* Notes */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Notes"
              multiline
              rows={4}
              value={contact.notes}
              onChange={handleInputChange('notes')}
            />
          </Grid>

          {/* Associated Opportunities Section - Only show for existing contacts */}
          {isEditing && (
            <Grid item xs={12}>
              <AssociatedOpportunities 
                contactId={contact.contactId} 
                title="Associated Opportunities"
              />
            </Grid>
          )}

          {/* Created By and Created Date (read-only for editing) */}
          {isEditing && (
            <>
              <Grid item xs={12} md={6}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Created By
                  </Typography>
                  <UserDisplay email={contact.createdByEmail} size="medium" />
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Created Date"
                  value={new Date(contact.createdDate).toLocaleString()}
                  InputProps={{ readOnly: true }}
                />
              </Grid>
            </>
          )}
        </Grid>

        <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button
            variant="outlined"
            onClick={handleBack}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Contact'}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default Contact; 